import { BUFFERING_DELAY_MS, DEFAULT_RATE, VOLUME_WRITE_DELAY_MS } from "./constants";
import { clamp } from "./format";
import { writeStoredVolume } from "./volumeStorage";

// Actions change the <audio> element (and the state that follows it). The flags and timers
// they need live here, so other modules go through these functions rather than shared variables.
export function createActions(player) {
    const { root, audio, el, cfg, state, track, render } = player;

    let hasTrackedError = false;
    let hasFocusInside = false;
    let bufferingTimer;
    let volumeTimer;
    let pendingVolume;

    /* ---------- error / focus ---------- */

    function setError(value) {
        if (state.hasError === value) return;
        state.hasError = value;
        if (value) {
            state.isPlaying = false;
            state.menuOpen = false;
            stopBuffering();
        }
        render();
        if (hasFocusInside) (value ? el.retry : el.play).focus();
    }

    function trackErrorOnce() {
        if (hasTrackedError) return;
        hasTrackedError = true;
        track("error");
    }

    root.addEventListener("focusin", () => {
        hasFocusInside = true;
    });
    root.addEventListener("focusout", e => {
        if (e.relatedTarget instanceof Node && root.contains(e.relatedTarget)) return;
        hasFocusInside = e.relatedTarget === null && e.target instanceof Element && e.target.matches(":disabled");
    });

    /* ---------- buffering ---------- */

    // Show the spinner only if buffering lasts longer than BUFFERING_DELAY_MS.
    function startBuffering() {
        if (bufferingTimer !== undefined) return;
        bufferingTimer = setTimeout(() => {
            bufferingTimer = undefined;
            state.isBuffering = true;
            render();
        }, BUFFERING_DELAY_MS);
    }

    function stopBuffering() {
        clearTimeout(bufferingTimer);
        bufferingTimer = undefined;
        state.isBuffering = false;
    }

    /* ---------- playback ---------- */

    function attemptPlay() {
        const p = audio.play();
        if (!p || !p.catch) return;
        p.catch(error => {
            state.isPlaying = false;
            if (error && error.name === "AbortError") {
                render();
                return;
            }
            setError(true);
            track("play_error", { message: error && error.message });
        });
    }

    function togglePlay() {
        if (state.isPlaying) audio.pause();
        else attemptPlay();
    }

    function seekTo(time, method) {
        const next = clamp(time, 0, state.duration);
        const from = audio.currentTime;
        audio.currentTime = next;
        state.currentTime = next;
        track("seek", { from, to: next, method });
        render();
    }
    const seek = t => seekTo(t, "scrub");
    const skip = secs => seekTo(audio.currentTime + secs, "skip");

    /* ---------- speed ---------- */

    function setPlaybackRate(rate) {
        const previous = audio.playbackRate;
        state.playbackRate = rate;
        audio.playbackRate = rate;
        track("speed_change", { from: previous, to: rate });
        render();
    }
    function stepPlaybackRate(direction) {
        if (!cfg.rates.length) return;
        const idx = cfg.rates.indexOf(state.playbackRate);
        const from = idx >= 0 ? idx : Math.max(cfg.rates.indexOf(DEFAULT_RATE), 0);
        setPlaybackRate(cfg.rates[clamp(from + direction, 0, cfg.rates.length - 1)]);
    }

    /* ---------- volume ---------- */

    // Volume is saved to localStorage after VOLUME_WRITE_DELAY_MS of no changes (or on page hide).
    function flushStoredVolume() {
        clearTimeout(volumeTimer);
        volumeTimer = undefined;
        if (pendingVolume === undefined) return;
        const v = pendingVolume;
        pendingVolume = undefined;
        writeStoredVolume(v);
    }
    function setVolume(next) {
        const v = clamp(next, 0, 1);
        state.volume = v;
        if (v > 0) {
            state.isMuted = false;
            audio.muted = false;
        }
        audio.volume = v;
        pendingVolume = v;
        clearTimeout(volumeTimer);
        volumeTimer = setTimeout(flushStoredVolume, VOLUME_WRITE_DELAY_MS);
        render();
    }
    function toggleMute() {
        audio.muted = !audio.muted;
        state.isMuted = audio.muted;
        render();
    }

    window.addEventListener("pagehide", flushStoredVolume);

    /* ---------- retry ---------- */

    function retry() {
        hasTrackedError = false;
        track("retry");
        const rate = audio.playbackRate;
        setError(false);
        audio.load();
        audio.playbackRate = rate;
        state.playbackRate = rate;
        attemptPlay();
        render();
    }

    return {
        setError,
        trackErrorOnce,
        startBuffering,
        stopBuffering,
        attemptPlay,
        togglePlay,
        seekTo,
        seek,
        skip,
        setPlaybackRate,
        stepPlaybackRate,
        setVolume,
        toggleMute,
        retry
    };
}
